import c from 'classnames';
import { IconAlertCircle } from 'featherico';
import moment from 'moment';
import { useCallback, useMemo, useState } from 'react';
import Calendar from '../../Calendar';
import styles from './styles.m.less';

interface Props {
    date: moment.Moment | undefined;
    onChange: (date: moment.Moment | undefined) => void;
    errorMessage: string | null;
    onMouseDown?: () => void;
    highlightedDates: string[];
    validRange: { from?: string; to?: string };
}

export default function Popup({ date, onChange, validRange, onMouseDown, highlightedDates, errorMessage }: Props) {
    const [month, setMonth] = useState((date ? date.clone() : moment()).startOf('month'));

    const handleDaySelect = useCallback((d: string) => onChange(fromDate(d)), [onChange]);

    const handlePrevMonthClick = useMemo(() => {
        if (!validRange.from || month.isAfter(validRange.from, 'month')) {
            return () => setMonth(m => m.clone().subtract(1, 'month'));
        } else {
            return undefined;
        }
    }, [month, validRange.from]);

    const handleNextMonthClick = useMemo(() => {
        if (!validRange.to || month.isBefore(validRange.to, 'month')) {
            return () => setMonth(m => m.clone().add(1, 'month'));
        } else {
            return undefined;
        }
    }, [month, validRange.to]);

    return (
        <div aria-hidden onMouseDown={onMouseDown}>
            <Calendar
                month={formatMonth(month)}
                from={date && formatDate(date)}
                to={date && formatDate(date)}
                onDayClick={handleDaySelect}
                onPrevMonthClick={handlePrevMonthClick}
                onNextMonthClick={handleNextMonthClick}
                highlightedDates={highlightedDates}
                validRange={validRange}
                fixedHeight={false}
            />
            {errorMessage && (
                <div className={c(styles.summary, { [styles.error]: errorMessage })}>
                    <>
                        <IconAlertCircle /> {errorMessage}
                    </>
                </div>
            )}
        </div>
    );
}

function formatDate(date: moment.Moment): string {
    return date.format(moment.HTML5_FMT.DATE);
}

function fromDate(date: string): moment.Moment {
    return moment(date, moment.HTML5_FMT.DATE);
}

function formatMonth(month: moment.Moment): string {
    return month.format('YYYY-MM');
}
